export const LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V1 = "loom.junction-fairing-intent/v1" as const;
export const LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2 = "loom.junction-fairing-intent/v2" as const;
export const LOOM_JUNCTION_FAIRING_INTENT_SCHEMA = LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V1;
export const LOOM_JUNCTION_FAIRING_FOOTPRINT_MIN_M = 0.02;
export const LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M = 0.5;
const LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_RATIO = 2.5;
export const LOOM_JUNCTION_FAIRING_G1_TANGENT = "g1-tangent" as const;
export const LOOM_JUNCTION_FAIRING_G2_CURVATURE = "g2-curvature" as const;

export type LoomJunctionFairingContinuity =
  | typeof LOOM_JUNCTION_FAIRING_G1_TANGENT
  | typeof LOOM_JUNCTION_FAIRING_G2_CURVATURE;

export interface LoomJunctionFairingRequestedContinuity {
  readonly parentA: LoomJunctionFairingContinuity;
  readonly parentB: LoomJunctionFairingContinuity;
}

export interface LoomJunctionFairingStation {
  readonly stationId: string;
  readonly junctionAngleDeg: number;
  readonly parentAOffsetM: number;
  readonly parentBOffsetM: number;
  readonly parentATangentScale: number;
  readonly parentBTangentScale: number;
}

interface LoomJunctionFairingAngleFrame {
  readonly owner: "aircraft" | "parentA" | "parentB";
  readonly normalAxis: "+x" | "-x" | "+y" | "-y" | "+z" | "-z";
  readonly zeroAxis: "+x" | "-x" | "+y" | "-y" | "+z" | "-z";
  readonly direction: "right-hand";
}

export interface LoomJunctionFairingIntentV1 {
  readonly schema: typeof LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V1;
  readonly transitionId: string;
  readonly construction: {
    readonly kind: "periodic-angle-tangent-offset-g1";
    readonly angleFrame: LoomJunctionFairingAngleFrame;
    readonly stations: readonly LoomJunctionFairingStation[];
  };
}

export interface LoomJunctionFairingIntentV2 {
  readonly schema: typeof LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2;
  readonly transitionId: string;
  readonly construction: {
    readonly kind: "periodic-angle-tangent-curvature-offset";
    readonly angleFrame: LoomJunctionFairingAngleFrame;
    readonly stations: readonly LoomJunctionFairingStation[];
    readonly requestedContinuity: LoomJunctionFairingRequestedContinuity;
  };
}

export type LoomJunctionFairingIntent = LoomJunctionFairingIntentV1 | LoomJunctionFairingIntentV2;

type Row = Record<string, unknown>;
const TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,159}$/;
const AXES = ["+x", "-x", "+y", "-y", "+z", "-z"] as const;

function object(value: unknown, path: string): Row {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Row;
}

function exact(value: Row, keys: readonly string[], path: string): void {
  const actual = Object.keys(value);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${path} has an unsupported field set`);
  }
}

function token(value: unknown, path: string): string {
  if (typeof value !== "string" || !TOKEN.test(value)) throw new Error(`${path} is invalid`);
  return value;
}

function finite(value: unknown, path: string, minimum: number, maximum: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < minimum || value > maximum) {
    throw new Error(`${path} must be a finite number in [${minimum}, ${maximum}]`);
  }
  return Object.is(value, -0) ? 0 : value;
}

export function parseLoomJunctionFairingIntent(value: unknown): LoomJunctionFairingIntent {
  const root = object(value, "junctionFairing");
  exact(root, ["schema", "transitionId", "construction"], "junctionFairing");
  const isV1 = root.schema === LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V1;
  const isV2 = root.schema === LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2;
  if (!isV1 && !isV2) {
    throw new Error("junctionFairing.schema is unsupported");
  }
  const construction = object(root.construction, "junctionFairing.construction");
  exact(construction, isV1 ? ["kind", "angleFrame", "stations"]
    : ["kind", "angleFrame", "stations", "requestedContinuity"], "junctionFairing.construction");
  const expectedKind = isV1
    ? "periodic-angle-tangent-offset-g1"
    : "periodic-angle-tangent-curvature-offset";
  if (construction.kind !== expectedKind) {
    throw new Error("junctionFairing construction kind is unsupported");
  }
  let requestedContinuity: LoomJunctionFairingRequestedContinuity | null = null;
  if (isV2) {
    const continuity = object(construction.requestedContinuity,
      "junctionFairing.construction.requestedContinuity");
    exact(continuity, ["parentA", "parentB"], "junctionFairing.construction.requestedContinuity");
    const supported = [LOOM_JUNCTION_FAIRING_G1_TANGENT, LOOM_JUNCTION_FAIRING_G2_CURVATURE];
    if (!supported.includes(continuity.parentA as LoomJunctionFairingContinuity)
        || !supported.includes(continuity.parentB as LoomJunctionFairingContinuity)) {
      throw new Error("junctionFairing requested continuity is unsupported");
    }
    if (continuity.parentA === LOOM_JUNCTION_FAIRING_G1_TANGENT
        && continuity.parentB === LOOM_JUNCTION_FAIRING_G1_TANGENT) {
      throw new Error("junctionFairing v2 must request curvature at at least one parent");
    }
    requestedContinuity = {
      parentA: continuity.parentA as LoomJunctionFairingContinuity,
      parentB: continuity.parentB as LoomJunctionFairingContinuity,
    };
  }
  const frame = object(construction.angleFrame, "junctionFairing.construction.angleFrame");
  exact(frame, ["owner", "normalAxis", "zeroAxis", "direction"], "junctionFairing.construction.angleFrame");
  if (!(["aircraft", "parentA", "parentB"] as const).includes(frame.owner as never)
      || !AXES.includes(frame.normalAxis as never) || !AXES.includes(frame.zeroAxis as never)
      || frame.direction !== "right-hand" || String(frame.normalAxis).slice(1) === String(frame.zeroAxis).slice(1)) {
    throw new Error("junctionFairing angle frame is invalid");
  }
  if (!Array.isArray(construction.stations)
      || construction.stations.length < 3 || construction.stations.length > 16) {
    throw new Error("junctionFairing requires 3 to 16 angular stations");
  }
  const stations = construction.stations.map((value, index): LoomJunctionFairingStation => {
    const path = `junctionFairing.construction.stations[${index}]`;
    const row = object(value, path);
    exact(row, ["stationId", "junctionAngleDeg", "parentAOffsetM", "parentBOffsetM",
      "parentATangentScale", "parentBTangentScale"], path);
    return {
      stationId: token(row.stationId, `${path}.stationId`),
      junctionAngleDeg: finite(row.junctionAngleDeg, `${path}.junctionAngleDeg`, 0, 359.99999999999994),
      parentAOffsetM: finite(row.parentAOffsetM, `${path}.parentAOffsetM`, 0, 1_000),
      parentBOffsetM: finite(row.parentBOffsetM, `${path}.parentBOffsetM`, 0, 1_000),
      parentATangentScale: finite(row.parentATangentScale, `${path}.parentATangentScale`, 0.05, 2),
      parentBTangentScale: finite(row.parentBTangentScale, `${path}.parentBTangentScale`, 0.05, 2),
    };
  }).sort((left, right) => left.junctionAngleDeg - right.junctionAngleDeg
    || left.stationId.localeCompare(right.stationId));
  if (new Set(stations.map(({ stationId }) => stationId)).size !== stations.length
      || new Set(stations.map(({ junctionAngleDeg }) => junctionAngleDeg)).size !== stations.length) {
    throw new Error("junctionFairing station IDs and angles must be unique");
  }
  const angleFrame: LoomJunctionFairingAngleFrame = {
    owner: frame.owner as LoomJunctionFairingAngleFrame["owner"],
    normalAxis: frame.normalAxis as LoomJunctionFairingAngleFrame["normalAxis"],
    zeroAxis: frame.zeroAxis as LoomJunctionFairingAngleFrame["zeroAxis"],
    direction: "right-hand",
  };
  const transitionId = token(root.transitionId, "junctionFairing.transitionId");
  if (isV1) return {
    schema: LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V1,
    transitionId,
    construction: {
      kind: "periodic-angle-tangent-offset-g1",
      angleFrame,
      stations,
    },
  };
  return {
    schema: LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2,
    transitionId,
    construction: {
      kind: "periodic-angle-tangent-curvature-offset",
      angleFrame,
      stations,
      requestedContinuity: requestedContinuity!,
    },
  };
}

export function createDefaultLoomJunctionFairingIntent(): LoomJunctionFairingIntent {
  const station = (stationId: string, junctionAngleDeg: number, parentAOffsetM: number,
    parentBOffsetM: number, parentATangentScale: number, parentBTangentScale: number) => ({
    stationId, junctionAngleDeg, parentAOffsetM, parentBOffsetM,
    parentATangentScale, parentBTangentScale,
  });
  return parseLoomJunctionFairingIntent({
    schema: LOOM_JUNCTION_FAIRING_INTENT_SCHEMA,
    transitionId: "main-wing-body-fairing",
    construction: {
      kind: "periodic-angle-tangent-offset-g1",
      angleFrame: { owner: "parentA", normalAxis: "-y", zeroAxis: "+x", direction: "right-hand" },
      stations: [
        station("aft", 0, 0.035, 0.05, 0.62, 0.7),
        station("upper", 90, 0.055, 0.08, 0.95, 1.1),
        station("forward", 180, 0.04, 0.06, 0.75, 0.75),
        station("lower", 270, 0.045, 0.065, 0.8, 0.9),
      ],
    },
  });
}

export function loomJunctionFairingRequestedContinuity(
  intent: LoomJunctionFairingIntent,
): LoomJunctionFairingRequestedContinuity {
  return intent.schema === LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2
    ? intent.construction.requestedContinuity
    : { parentA: LOOM_JUNCTION_FAIRING_G1_TANGENT, parentB: LOOM_JUNCTION_FAIRING_G1_TANGENT };
}

export function replaceLoomJunctionFairingRequestedContinuity(
  intent: LoomJunctionFairingIntent,
  requestedContinuity: LoomJunctionFairingRequestedContinuity,
): LoomJunctionFairingIntent {
  const isG1 = requestedContinuity.parentA === LOOM_JUNCTION_FAIRING_G1_TANGENT
    && requestedContinuity.parentB === LOOM_JUNCTION_FAIRING_G1_TANGENT;
  return parseLoomJunctionFairingIntent({
    schema: isG1 ? LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V1 : LOOM_JUNCTION_FAIRING_INTENT_SCHEMA_V2,
    transitionId: intent.transitionId,
    construction: {
      kind: isG1 ? "periodic-angle-tangent-offset-g1" : "periodic-angle-tangent-curvature-offset",
      angleFrame: intent.construction.angleFrame,
      stations: intent.construction.stations,
      ...(isG1 ? {} : { requestedContinuity }),
    },
  });
}

export function replaceLoomJunctionFairingStation(
  intent: LoomJunctionFairingIntent,
  stationId: string,
  patch: Partial<Omit<LoomJunctionFairingStation, "stationId">>,
): LoomJunctionFairingIntent {
  if (!intent.construction.stations.some((station) => station.stationId === stationId)) {
    throw new Error(`junctionFairing station ${stationId} does not exist`);
  }
  return parseLoomJunctionFairingIntent({
    ...intent,
    construction: {
      ...intent.construction,
      stations: intent.construction.stations.map((station) => station.stationId === stationId
        ? { ...station, ...patch, stationId }
        : station),
    },
  });
}

export function loomJunctionFairingStationExtentM(station: LoomJunctionFairingStation): number {
  return station.parentAOffsetM + station.parentBOffsetM;
}

export function replaceLoomJunctionFairingStationExtent(
  intent: LoomJunctionFairingIntent,
  stationId: string,
  extentM: number,
): LoomJunctionFairingIntent {
  if (!Number.isFinite(extentM) || extentM < 0 || extentM > LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M) {
    throw new Error(`junctionFairing footprint extent must be in [0, ${LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M}]`);
  }
  const station = intent.construction.stations.find((candidate) => candidate.stationId === stationId);
  if (!station) throw new Error(`junctionFairing station ${stationId} does not exist`);
  const currentExtent = loomJunctionFairingStationExtentM(station);
  const parentARatio = currentExtent === 0 ? 0.5 : station.parentAOffsetM / currentExtent;
  return replaceLoomJunctionFairingStation(intent, stationId, {
    parentAOffsetM: extentM * parentARatio,
    parentBOffsetM: extentM * (1 - parentARatio),
  });
}

export function loomJunctionFairingFootprintIssue(intent: LoomJunctionFairingIntent): string | null {
  const extents = intent.construction.stations.map(loomJunctionFairingStationExtentM);
  if (extents.some((extent) => extent < LOOM_JUNCTION_FAIRING_FOOTPRINT_MIN_M)) {
    return `Keep every direction at or above ${LOOM_JUNCTION_FAIRING_FOOTPRINT_MIN_M} m, or remove the fairing.`;
  }
  if (extents.some((extent) => extent > LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M)) {
    return `Keep every direction at or below ${LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_M} m.`;
  }
  const minimum = Math.min(...extents);
  const maximum = Math.max(...extents);
  if (maximum / minimum > LOOM_JUNCTION_FAIRING_FOOTPRINT_MAX_RATIO) {
    return "Balance the four extents before applying; the widest direction may be at most 2.5× the narrowest.";
  }
  return null;
}
